/**
 * PipelineCommitmentCard — Pipeline health card showing:
 * 1. High probability (≥90%) pipeline
 * 2. Blood-commit pipeline (salesperson pledge)
 * 3. Quarter-wise expected closing breakdown
 */
import React, { useState, useEffect } from 'react';
import { crmAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { Skeleton } from '../ui/skeleton';
import { buildHealthCardParams } from './healthCardParams';
import { HealthFilterScopeRibbon } from './HealthFilterScopeRibbon';
import { Target, TrendingUp, Shield, Calendar, BarChart2 } from 'lucide-react';

function Ring({ pct, size = 100, sw = 10, color }) {
  const r = (size - sw) / 2;
  const circ = 2 * Math.PI * r;
  const fill = Math.min(pct, 100) / 100 * circ;
  const c = color || (pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#dc2626');
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={sw} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth={sw}
        strokeDasharray={`${fill} ${circ - fill}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.9s ease' }} />
    </svg>
  );
}

function fmt(v, currency) {
  if (!v && v !== 0) return '—';
  const prefix = currency || 'OMR';
  if (v >= 1e6) return `${prefix} ${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${prefix} ${(v / 1e3).toFixed(1)}K`;
  return `${prefix} ${v.toFixed(0)}`;
}

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'high_prob', label: '≥90%' },
  { id: 'commit', label: 'Committed' },
  { id: 'quarters', label: 'By Quarter' },
];

export function PipelineCommitmentCard({ filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('summary');
  const { currency } = useCurrency();

  useEffect(() => {
    const params = buildHealthCardParams(filters);
    setLoading(true);
    crmAPI.getPipelineCommitmentHealth(params)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [filters]);

  if (loading) return (
    <div className="h-full rounded-2xl border border-slate-100 bg-white p-4 space-y-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <Skeleton className="h-5 w-56" />
      <Skeleton className="h-28 w-full rounded-xl" />
      <div className="grid grid-cols-3 gap-2">{[0,1,2].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
    </div>
  );

  if (!data) return (
    <div className="h-full rounded-2xl border border-slate-100 bg-white flex items-center justify-center p-6 text-sm text-slate-400">
      No pipeline data available
    </div>
  );

  const s = data.summary || {};
  const highProbPct = s.high_prob_pct || 0;
  const commitPct = s.blood_commit_pct || 0;

  return (
    <div className="h-full rounded-2xl border border-slate-100 bg-white flex flex-col overflow-hidden"
      style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }} data-testid="pipeline-commitment-health-card">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
            <Target className="h-4 w-4" style={{ color: '#800000' }} />
            Pipeline Commitment Health
          </h3>
          <div className="flex gap-0.5">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded-lg transition-all ${tab === t.id ? 'text-white' : 'text-muted-foreground hover:bg-slate-100'}`}
                style={tab === t.id ? { backgroundColor: '#800000' } : {}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <HealthFilterScopeRibbon filters={filters} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        {tab === 'summary' && <SummaryView s={s} highProbPct={highProbPct} commitPct={commitPct} currency={currency} />}
        {tab === 'high_prob' && <HighProbView data={data.high_probability} currency={currency} />}
        {tab === 'commit' && <CommitView data={data.blood_commit} currency={currency} />}
        {tab === 'quarters' && <QuarterView data={data.quarter_closing} currency={currency} />}
      </div>
    </div>
  );
}

function SummaryView({ s, highProbPct, commitPct, currency }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-slate-50 p-3 text-center">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Pipeline</p>
          <p className="text-lg font-bold text-slate-800 font-mono">{fmt(s.total_pipeline, currency)}</p>
          <p className="text-[10px] text-slate-400">{s.total_count} deals</p>
        </div>
        <div className="rounded-xl bg-emerald-50 p-3 text-center">
          <p className="text-[10px] text-emerald-600 uppercase tracking-wider font-semibold">≥90% Prob</p>
          <p className="text-lg font-bold text-emerald-700 font-mono">{fmt(s.high_prob_value, currency)}</p>
          <p className="text-[10px] text-emerald-500">{s.high_prob_count} deals</p>
        </div>
        <div className="rounded-xl bg-amber-50 p-3 text-center">
          <p className="text-[10px] text-amber-600 uppercase tracking-wider font-semibold">Committed</p>
          <p className="text-lg font-bold text-amber-700 font-mono">{fmt(s.blood_commit_value, currency)}</p>
          <p className="text-[10px] text-amber-500">{s.blood_commit_count} deals</p>
        </div>
      </div>

      <div className="flex items-center justify-center gap-8">
        <div className="text-center relative">
          <Ring pct={highProbPct} size={90} sw={9} color="#10b981" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-base font-bold text-emerald-700">{highProbPct}%</span>
            <span className="text-[9px] text-slate-500">High Prob</span>
          </div>
        </div>
        <div className="text-center relative">
          <Ring pct={commitPct} size={90} sw={9} color="#f59e0b" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-base font-bold text-amber-700">{commitPct}%</span>
            <span className="text-[9px] text-slate-500">Committed</span>
          </div>
        </div>
      </div>

      <div className="text-[10px] text-slate-400 text-center">
        High probability = deals with ≥90% win probability. Committed = salesperson blood commit / pledge.
      </div>
    </div>
  );
}

function HighProbView({ data, currency }) {
  if (!data) return null;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-emerald-50 p-3">
          <div className="flex items-center gap-1.5 mb-1"><TrendingUp className="h-3.5 w-3.5 text-emerald-600" /><span className="text-[10px] text-emerald-600 font-semibold uppercase">Pipeline Value</span></div>
          <p className="text-lg font-bold text-emerald-700 font-mono">{fmt(data.value, currency)}</p>
          <p className="text-[10px] text-emerald-500">{data.count} deals at ≥90%</p>
        </div>
        <div className="rounded-xl bg-blue-50 p-3">
          <div className="flex items-center gap-1.5 mb-1"><BarChart2 className="h-3.5 w-3.5 text-blue-600" /><span className="text-[10px] text-blue-600 font-semibold uppercase">Expected Revenue</span></div>
          <p className="text-lg font-bold text-blue-700 font-mono">{fmt(data.expected, currency)}</p>
        </div>
      </div>
      {(data.by_owner || []).length > 0 && (
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-semibold">By Owner</p>
          <div className="space-y-1.5">
            {data.by_owner.map((o, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-slate-700 truncate flex-1">{o.name}</span>
                <span className="text-slate-500 tabular-nums">{o.count} deals</span>
                <span className="font-semibold text-slate-800 tabular-nums ml-3 min-w-[80px] text-right">{fmt(o.value, currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CommitView({ data, currency }) {
  if (!data) return null;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-amber-50 p-3">
          <div className="flex items-center gap-1.5 mb-1"><Shield className="h-3.5 w-3.5 text-amber-600" /><span className="text-[10px] text-amber-600 font-semibold uppercase">Committed Value</span></div>
          <p className="text-lg font-bold text-amber-700 font-mono">{fmt(data.value, currency)}</p>
          <p className="text-[10px] text-amber-500">{data.count} pledged deals</p>
        </div>
        <div className="rounded-xl bg-blue-50 p-3">
          <div className="flex items-center gap-1.5 mb-1"><BarChart2 className="h-3.5 w-3.5 text-blue-600" /><span className="text-[10px] text-blue-600 font-semibold uppercase">Expected Revenue</span></div>
          <p className="text-lg font-bold text-blue-700 font-mono">{fmt(data.expected, currency)}</p>
        </div>
      </div>
      {data.count === 0 && (
        <div className="text-center py-6 text-xs text-slate-400">
          <Shield className="h-8 w-8 mx-auto mb-2 text-slate-200" />
          No blood-commit / pledge deals found in the current pipeline.
        </div>
      )}
      {(data.by_owner || []).length > 0 && (
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-semibold">By Owner</p>
          <div className="space-y-1.5">
            {data.by_owner.map((o, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-slate-700 truncate flex-1">{o.name}</span>
                <span className="text-slate-500 tabular-nums">{o.count} deals</span>
                <span className="font-semibold text-slate-800 tabular-nums ml-3 min-w-[80px] text-right">{fmt(o.value, currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function QuarterView({ data, currency }) {
  if (!data || data.length === 0) return (
    <div className="text-center py-6 text-xs text-slate-400">
      <Calendar className="h-8 w-8 mx-auto mb-2 text-slate-200" />
      No quarter-wise closing data available.
    </div>
  );
  const maxVal = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="space-y-3">
      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Expected Closing by Quarter</p>
      <div className="space-y-2">
        {data.map((q, i) => (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-700">{q.quarter}</span>
              <span className="text-slate-500 tabular-nums">{q.count} deals</span>
              <span className="font-bold text-slate-800 tabular-nums ml-3">{fmt(q.value, currency)}</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${Math.max((q.value / maxVal) * 100, 2)}%`,
                  background: q.quarter === 'No Date' ? '#94a3b8' : i % 3 === 0 ? '#800000' : i % 3 === 1 ? '#3b82f6' : '#10b981',
                }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
